import {useState, useCallback, useMemo} from 'react';

import {accessItemTypeEnum, checkboxStateEnum, accessGroupEnum} from '../../../serverClientShared/sharedEnums';
import useContentTree from '../../Generic/ContentTree/useContentTree';
import {getCheckboxState, updateCheckbox} from './accessSelectionHelpers';
import {
    EMPTY_ORIGINAL, EMPTY_EDITS, EMPTY_MIXED,
    buildNodeInfoMap, buildChildrenByParent, checkItemDisabled, getItemDisabledReason,
} from './accessNodeHelpers';
import useAccessSearch from './useAccessSearch';
import useAccessFetch from './useAccessFetch';
import useAccessInit from './useAccessInit';
import useAccessInfo from './useAccessInfo';
import useAccessPayload from './useAccessPayload';
import useAccessSelection from './useAccessSelection';

const useAccessModal = (workspace, {targetId, targetIds: targetIdsProp, targetType, groups = [], targets = [], isRoleVisibilityEnabled = false, savedState = null, accessSummary = null} = {}) => {
    const targetIds = useMemo(
        () => targetIdsProp || (targetId ? [targetId] : []),
        [targetIdsProp, targetId],
    );
    const isBulkMode = targetIds.length > 1;

    const tree = useContentTree(workspace, {includePublishedRoles: true, fetchAllProjects: true});
    const [original, setOriginal] = useState(EMPTY_ORIGINAL);
    const [edits, setEdits] = useState(EMPTY_EDITS);
    const [mixed, setMixed] = useState(EMPTY_MIXED);
    const [accessMap, setAccessMap] = useState({});
    const [inheritedSources, setInheritedSources] = useState({});
    const [inheritedIds, setInheritedIds] = useState([]);
    const [groupMembership, setGroupMembership] = useState(new Map());
    const [removeAll, setRemoveAll] = useState(false);
    const [selectionSummary, setSelectionSummary] = useState(accessSummary || {projects: 0, folders: 0, items: 0});
    const [savedNodeInfoMap, setSavedNodeInfoMap] = useState(new Map());

    const {applyAccessEntries, fetchAccessForNodes} = useAccessFetch({
        workspace, targetIds, inheritedIds, isBulkMode,
        accessMap, setAccessMap, inheritedSources,
        original, setOriginal, setMixed, edits, tree,
    });

    const {isSearchActive, searchNodeInfoMap, handleSearch, searchTree, resetSearch} = useAccessSearch(
        workspace, {targetIds, inheritedIds, applyAccessEntries},
    );

    const baseNodeInfoMap = useMemo(
        () => buildNodeInfoMap(tree.projects, tree.childrenCache),
        [tree.projects, tree.childrenCache],
    );

    const nodeInfoMap = useMemo(() => {
        const merged = new Map(savedNodeInfoMap);
        for (const [id, info] of baseNodeInfoMap) merged.set(id, info);
        for (const [id, info] of searchNodeInfoMap) {
            if (!merged.has(id)) merged.set(id, info);
        }
        return merged;
    }, [savedNodeInfoMap, baseNodeInfoMap, searchNodeInfoMap]);

    const childrenByParent = useMemo(
        () => buildChildrenByParent(nodeInfoMap),
        [nodeInfoMap],
    );

    const combinedState = useMemo(() => ({
        originalIncludeAll: original.includeAll,
        originalNotIncludeAll: original.notIncludeAll,
        addedIncludeAll: edits.addedIncludeAll,
        addedIndividual: edits.addedIndividual,
        removed: edits.removed,
    }), [original, edits]);

    const {initializeModal} = useAccessInit({
        tree, groups, targets, isBulkMode, savedState, accessSummary,
        setOriginal, setEdits, setMixed, setAccessMap, setRemoveAll,
        setInheritedIds, setInheritedSources, setGroupMembership, setSavedNodeInfoMap,
        setSelectionSummary, resetSearch,
    });

    const handleExpand = useCallback(async (nodeId, nodeType, nestedRoute) => {
        const isExpanding = !tree.expandedNodes.has(nodeId);
        await tree.handleExpand(nodeId, nodeType, nestedRoute);
        if (isExpanding) await fetchAccessForNodes(nodeId, nestedRoute);
    }, [tree, fetchAccessForNodes]);

    const wrappedTree = useMemo(() => ({...tree, handleExpand}), [tree, handleExpand]);

    const activeTree = useMemo(() => {
        if (isSearchActive && searchTree) {
            const mergedExpanded = new Set([...searchTree.expandedNodes, ...tree.expandedNodes]);
            const mergedChildren = {...tree.childrenCache, ...searchTree.childrenCache};
            return {...searchTree, handleExpand, expandedNodes: mergedExpanded, childrenCache: mergedChildren};
        }
        return wrappedTree;
    }, [isSearchActive, searchTree, wrappedTree, handleExpand, tree.expandedNodes, tree.childrenCache]);

    const {getAccessInfo} = useAccessInfo(accessMap, nodeInfoMap);

    const checkboxState = useCallback(
        (nodeId) => getCheckboxState(nodeId, combinedState, nodeInfoMap),
        [combinedState, nodeInfoMap],
    );

    const isNodeGranted = useCallback((id) => {
        if (edits.removed.has(id)) return false;
        if (edits.addedIncludeAll.has(id) || edits.addedIndividual.has(id)) return true;
        return original.includeAll.has(id) || original.notIncludeAll.has(id);
    }, [edits, original]);

    const grantsToSubtree = useCallback((id) => {
        const state = checkboxState(id);
        if (state === checkboxStateEnum.CHECKED || state === checkboxStateEnum.INCLUDE_ALL) return true;
        if (isNodeGranted(id)) return true;
        return !!getAccessInfo(id)?.inheritedFrom?.some(s => s.sourceId !== accessGroupEnum.WORKSPACE_PUBLIC);
    }, [checkboxState, isNodeGranted, getAccessInfo]);

    const isOwnPublic = useCallback(
        (id) => !!accessMap[id]?.inheritedFrom?.some(s => s.sourceId === accessGroupEnum.WORKSPACE_PUBLIC),
        [accessMap],
    );

    const isParentReachable = useCallback((nodeId) => {
        let current = nodeInfoMap.get(nodeId)?.parentId;
        while (current) {
            if (grantsToSubtree(current)) return true;
            if (!isOwnPublic(current)) return false;
            const next = nodeInfoMap.get(current)?.parentId;
            if (!next) return true;
            current = next;
        }
        return false;
    }, [nodeInfoMap, grantsToSubtree, isOwnPublic]);

    const isSharedEffective = useCallback((nodeId) => {
        if (nodeInfoMap.get(nodeId)?.type === accessItemTypeEnum.PROJECT) return true;
        return isParentReachable(nodeId);
    }, [nodeInfoMap, isParentReachable]);

    const isAncestorInheritedIncludeAll = useCallback((id) => {
        const nodeInfo = nodeInfoMap.get(id);
        if (!nodeInfo?.parentId) return false;
        if (accessMap[nodeInfo.parentId]?.inheritedIncludeAll) return true;
        return isAncestorInheritedIncludeAll(nodeInfo.parentId);
    }, [accessMap, nodeInfoMap]);

    const hasInheritedIncludeAll = useCallback((id) => {
        if (accessMap[id]?.inheritedIncludeAll) return true;
        return isAncestorInheritedIncludeAll(id);
    }, [accessMap, isAncestorInheritedIncludeAll]);

    const hasInheritedAccess = useCallback((id) => {
        if (accessMap[id]?.inheritedFrom?.length > 0) return true;
        return isAncestorInheritedIncludeAll(id);
    }, [accessMap, isAncestorInheritedIncludeAll]);

    const countSummaryContribution = useCallback((state) => {
        const s = {projects: 0, folders: 0, items: 0};
        for (const id of state.addedIncludeAll) {
            if (hasInheritedIncludeAll(id)) continue;
            const type = nodeInfoMap.get(id)?.type;
            if (type === accessItemTypeEnum.PROJECT) s.projects++;
            else if (type === accessItemTypeEnum.FOLDER) s.folders++;
        }
        for (const id of state.addedIndividual) {
            if (hasInheritedAccess(id)) continue;
            if (nodeInfoMap.get(id)?.type === accessItemTypeEnum.ITEM) s.items++;
        }
        for (const id of original.includeAll) {
            if (state.removed.has(id)) continue;
            if (state.addedIncludeAll.has(id)) continue;
            if (state.addedIndividual.has(id)) continue;
            if (hasInheritedIncludeAll(id)) continue;
            const type = nodeInfoMap.get(id)?.type;
            if (type === accessItemTypeEnum.PROJECT) s.projects++;
            else if (type === accessItemTypeEnum.FOLDER) s.folders++;
        }
        for (const id of original.notIncludeAll) {
            if (state.removed.has(id)) continue;
            if (state.addedIndividual.has(id)) continue;
            if (hasInheritedAccess(id)) continue;
            if (nodeInfoMap.get(id)?.type === accessItemTypeEnum.ITEM) s.items++;
        }
        return s;
    }, [nodeInfoMap, original, hasInheritedIncludeAll, hasInheritedAccess]);

    const handleCheckToggle = useCallback((nodeId) => {
        const currentState = getCheckboxState(nodeId, combinedState, nodeInfoMap);
        setEdits((prev) => {
            const next = {
                originalIncludeAll: original.includeAll,
                originalNotIncludeAll: original.notIncludeAll,
                addedIncludeAll: new Set(prev.addedIncludeAll),
                addedIndividual: new Set(prev.addedIndividual),
                removed: new Set(prev.removed),
            };
            updateCheckbox(nodeId, currentState, next, nodeInfoMap, childrenByParent, isBulkMode);
            const before = countSummaryContribution(prev);
            const after = countSummaryContribution(next);
            setSelectionSummary((s) => ({
                projects: Math.max(0, s.projects + after.projects - before.projects),
                folders: Math.max(0, s.folders + after.folders - before.folders),
                items: Math.max(0, s.items + after.items - before.items),
            }));
            return next;
        });
    }, [combinedState, nodeInfoMap, childrenByParent, original, isBulkMode, countSummaryContribution]);

    const isItemDisabled = useCallback(
        (nodeId) => checkItemDisabled(nodeId, nodeInfoMap, targets, isRoleVisibilityEnabled),
        [nodeInfoMap, targets, isRoleVisibilityEnabled],
    );

    const itemDisabledReason = useCallback(
        (nodeId) => getItemDisabledReason(nodeId, nodeInfoMap, targets, isRoleVisibilityEnabled),
        [nodeInfoMap, targets, isRoleVisibilityEnabled],
    );

    const {buildSavePayload, buildFilterPayload} = useAccessPayload({
        nodeInfoMap, edits, targetIds, removeAll, isRoleVisibilityEnabled, getTreeSnapshot: tree.getSnapshot,
    });

    const {isAllSelected, handleToggleAll} = useAccessSelection({
        activeTree, original, edits, setEdits, isSearchActive, setRemoveAll,
        countSummaryContribution, setSelectionSummary,
    });

    const resetModal = useCallback(() => {
        setOriginal(EMPTY_ORIGINAL);
        setEdits(EMPTY_EDITS);
        setMixed(EMPTY_MIXED);
        setAccessMap({});
        setInheritedSources({});
        setInheritedIds([]);
        setGroupMembership(new Map());
        setRemoveAll(false);
        setSelectionSummary({projects: 0, folders: 0, items: 0});
        setSavedNodeInfoMap(new Map());
        resetSearch();
    }, [resetSearch]);

    const baselineState = useMemo(() => ({
        originalIncludeAll: original.includeAll,
        originalNotIncludeAll: original.notIncludeAll,
        addedIncludeAll: new Set(),
        addedIndividual: new Set(),
        removed: new Set(),
    }), [original]);

    const hasEdits = useMemo(() => {
        if (removeAll) return original.includeAll.size > 0 || original.notIncludeAll.size > 0;
        const ids = new Set([
            ...original.includeAll, ...original.notIncludeAll,
            ...edits.addedIncludeAll, ...edits.addedIndividual, ...edits.removed,
        ]);
        for (const id of ids) {
            if (getCheckboxState(id, combinedState, nodeInfoMap) !== getCheckboxState(id, baselineState, nodeInfoMap)) {
                return true;
            }
        }
        return false;
    }, [original, edits, removeAll, combinedState, baselineState, nodeInfoMap]);

    return {
        tree: activeTree, checkboxState, getAccessInfo, initializeModal, resetModal,
        handleCheckToggle, handleToggleAll, isAllSelected, removeAll, hasEdits,
        buildSavePayload, buildFilterPayload, selectionSummary,
        isBulkMode, mixed, edits, targets, groupMembership, isSharedEffective,
        isItemDisabled, itemDisabledReason, isSearchActive, handleSearch,
    };
};

export default useAccessModal;
